import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AddQuestForm } from './components/AddQuestForm';
import { Modal } from './components/Modal';
import { NavBar } from './components/NavBar';
import { Panel } from './components/Panel';
import { AddQuestProvider, useAddQuest } from './lib/addQuest';
import { AuthProvider, useAuth } from './lib/auth';
import { StoreProvider, useStore } from './lib/store';
import { ThemeProvider } from './lib/theme';
import { AuthScreen } from './screens/AuthScreen';
import { CalendarScreen } from './screens/CalendarScreen';
import { DetailScreen } from './screens/DetailScreen';
import { LandingScreen, type LandingMode } from './screens/LandingScreen';
import { MapScreen } from './screens/MapScreen';
import { QuestsScreen } from './screens/QuestsScreen';
import { ResetScreen } from './screens/ResetScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { StatsScreen } from './screens/StatsScreen';
import { TodayScreen } from './screens/TodayScreen';

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </ThemeProvider>
  );
}

/** Chooses between the landing page, auth screen, and logged-in shell. */
function Gate() {
  const { user, ready } = useAuth();
  const showBooting = useDelayedFlag(!ready);
  const [authMode, setAuthMode] = useState<LandingMode | null>(null);

  // A password-reset link is reachable while logged out. Match on the token
  // query param as well as the path: some Pages setups rewrite /reset?token=…
  // down to /?token=…, so keying off the param makes the flow work either way.
  if (
    typeof window !== 'undefined' &&
    (window.location.pathname === '/reset' ||
      new URLSearchParams(window.location.search).has('token'))
  ) {
    return <ResetScreen />;
  }

  if (!ready) return showBooting ? <Booting /> : null;
  if (!user) {
    return authMode
      ? <AuthScreen initialMode={authMode} onBack={() => setAuthMode(null)} />
      : <LandingScreen onEnter={setAuthMode} />;
  }

  // Remount the store when the user changes so we never flash another
  // account's cached goals after a logout/login swap.
  return (
    <StoreProvider key={user.id}>
      <AddQuestProvider>
        <Shell />
      </AddQuestProvider>
    </StoreProvider>
  );
}

function Shell() {
  const { goals, loading, error } = useStore();
  const showLoading = useDelayedFlag(loading);

  const dayCount = useMemo(() => {
    if (goals.length === 0) return 1;
    const earliest = goals.reduce(
      (m, g) => Math.min(m, new Date(g.created_at).getTime()),
      Date.now(),
    );
    return Math.max(1, Math.floor((Date.now() - earliest) / (1000 * 60 * 60 * 24)) + 1);
  }, [goals]);

  return (
    <div className="app">
      <NavBar dayCount={dayCount} />

      {error && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#4a1810',
            color: '#f5e4b8',
            border: '3px solid #000',
            fontFamily: 'VT323',
            fontSize: 20,
          }}
        >
          ▸ {error}
        </div>
      )}

      <div className="screen-anchor" key={location.pathname}>
        {loading ? (
          showLoading ? <Loading /> : null
        ) : (
          <Routes>
            <Route path="/" element={<TodayScreen />} />
            <Route path="/quests" element={<QuestsScreen />} />
            {/* The tended view now lives inside the CHORES page; keep the old
                deep link working by bouncing it there. */}
            <Route path="/tended" element={<Navigate to="/quests" replace />} />
            <Route path="/quest/:id" element={<DetailScreen />} />
            <Route path="/stats" element={<StatsScreen />} />
            <Route path="/calendar" element={<CalendarScreen />} />
            <Route path="/map" element={<MapScreen />} />
            <Route path="/settings" element={<SettingsScreen />} />
            <Route path="*" element={<TodayScreen />} />
          </Routes>
        )}
      </div>

      <AddQuestModal />

      <div className="footer">
        ▲▼ navigate &nbsp;•&nbsp; [Z] confirm &nbsp;•&nbsp; [X] back &nbsp;•&nbsp; habit hollow v0.1
      </div>
    </div>
  );
}

function AddQuestModal() {
  const { open, closeModal } = useAddQuest();
  return (
    <Modal open={open} onClose={closeModal}>
      <Panel title="PLANT A SEED" bodyClassName="panel-body">
        <AddQuestForm onDone={closeModal} onCancel={closeModal} />
      </Panel>
    </Modal>
  );
}

/**
 * Defers a boolean so brief loading states don't flash on screen.
 * Waits `delay` ms before turning true, and once true stays for at least
 * `minShow` ms — so the user either never sees the loader, or sees it
 * long enough that it doesn't feel like a glitch.
 */
function useDelayedFlag(
  active: boolean,
  { delay = 250, minShow = 350 }: { delay?: number; minShow?: number } = {},
): boolean {
  const [shown, setShown] = useState(false);
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    if (active && !shown) {
      const t = window.setTimeout(() => {
        shownAt.current = Date.now();
        setShown(true);
      }, delay);
      return () => window.clearTimeout(t);
    }
    if (!active && shown) {
      const elapsed = shownAt.current ? Date.now() - shownAt.current : minShow;
      const remaining = Math.max(0, minShow - elapsed);
      const t = window.setTimeout(() => {
        shownAt.current = null;
        setShown(false);
      }, remaining);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [active, shown, delay, minShow]);

  return shown;
}

function Booting() {
  return (
    <div className="pixel-panel pixel-panel--parchment" style={{
      padding: 40, textAlign: 'center',
      maxWidth: 320, margin: '15vh auto',
    }}>
      <div className="pixel-font" style={{ fontSize: 12, color: '#7a2a1f', marginBottom: 10 }}>
        WAKING UP…
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="pixel-panel pixel-panel--parchment" style={{ padding: 40, textAlign: 'center' }}>
      <div className="pixel-font" style={{ fontSize: 14, color: '#7a2a1f', marginBottom: 10 }}>
        LOADING…
      </div>
      <div style={{ fontFamily: 'VT323', fontSize: 20, color: '#6b3a1d' }}>
        Opening your little chore book.
      </div>
    </div>
  );
}
